import type { DailyCall, DailyParticipant } from '@daily-co/daily-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ClientIdentity } from '../../shared/types'

export type DailyVideoState = 'idle' | 'joining' | 'joined' | 'error'
export type BackgroundEffect = 'none' | 'blur' | 'image'

interface DailyAccess {
  url: string
  token?: string
}

function participantIdentity(participant: DailyParticipant): string | null {
  if (/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(participant.user_id)) {
    return participant.user_id
  }
  const data = participant.userData
  if (data && typeof data === 'object' && 'identityId' in data) {
    const identityId = (data as { identityId?: unknown }).identityId
    if (typeof identityId === 'string') return identityId
  }
  return null
}

/** Keeps the paid video transport isolated from the product UI and data layer. */
export function useDailyVideo(
  enabled: boolean,
  identity: ClientIdentity | null,
  localStream: MediaStream | null,
  cameraOn: boolean,
  visibleIdentityIds: string[],
) {
  const callRef = useRef<DailyCall | null>(null)
  const visibleRef = useRef(new Set(visibleIdentityIds))
  const localStreamRef = useRef<MediaStream | null>(localStream)
  const cameraOnRef = useRef(cameraOn)
  const retryCountRef = useRef(0)
  const lastEffectRef = useRef<{ effect: BackgroundEffect; source?: ArrayBuffer }>({ effect: 'none' })
  const [generation, setGeneration] = useState(0)
  const [state, setState] = useState<DailyVideoState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(
    new Map(),
  )
  const [processedLocalStream, setProcessedLocalStream] = useState<MediaStream | null>(null)
  const [backgroundEffect, setBackgroundEffect] = useState<BackgroundEffect>('none')

  const applyBackground = useCallback(async (effect: BackgroundEffect, source?: ArrayBuffer) => {
    const call = callRef.current
    if (!call || call.isDestroyed()) return false
    try {
      await call.updateInputSettings({
        video: {
          processor: effect === 'none'
            ? { type: 'none' }
            : effect === 'blur'
              ? { type: 'background-blur', config: { strength: 0.72 } }
              : { type: 'background-image', config: { source: source as ArrayBuffer } },
        },
      })
      const localTrack = call.participants().local?.tracks.video.persistentTrack
      setProcessedLocalStream(localTrack ? new MediaStream([localTrack]) : null)
      lastEffectRef.current = { effect, source }
      setBackgroundEffect(effect)
      return true
    } catch {
      lastEffectRef.current = { effect: 'none' }
      setBackgroundEffect('none')
      return false
    }
  }, [])

  useEffect(() => {
    cameraOnRef.current = cameraOn
    const call = callRef.current
    if (!call || call.isDestroyed()) return
    call.setLocalAudio(false)
    call.setLocalVideo(cameraOn)
  }, [cameraOn])

  // Swap the camera track in place; a device change must not tear down the whole call.
  useEffect(() => {
    const previous = localStreamRef.current
    localStreamRef.current = localStream
    const call = callRef.current
    if (!call || call.isDestroyed() || previous === localStream) return
    const track = localStream?.getVideoTracks()[0]
    void call
      .setInputDevicesAsync({ videoSource: track ?? false })
      .then(() => {
        const active = callRef.current
        if (!active || active.isDestroyed()) return
        const localTrack = active.participants().local?.tracks.video.persistentTrack
        setProcessedLocalStream(localTrack ? new MediaStream([localTrack]) : null)
      })
      .catch(() => undefined)
  }, [localStream])

  useEffect(() => {
    visibleRef.current = new Set(visibleIdentityIds)
    const call = callRef.current
    if (!call || call.isDestroyed()) return
    const updates = Object.fromEntries(
      Object.values(call.participants())
        .filter((participant) => !participant.local)
        .map((participant) => [
          participant.session_id,
          {
            setSubscribedTracks: visibleRef.current.has(participantIdentity(participant) ?? '')
              ? { audio: false, video: true, screenAudio: false, screenVideo: false }
              : false,
          },
        ]),
    )
    if (Object.keys(updates).length) call.updateParticipants(updates)
  }, [visibleIdentityIds])

  useEffect(() => {
    if (!enabled || !identity) {
      retryCountRef.current = 0
      setState('idle')
      setRemoteStreams(new Map())
      setProcessedLocalStream(null)
      setBackgroundEffect('none')
      return
    }
    // Join even without a camera: the room is still watchable receive-only.
    const track = localStreamRef.current?.getVideoTracks()[0]

    let disposed = false
    let renewTimer: number | null = null
    let retryTimer: number | null = null
    let call: DailyCall | null = null
    setState('joining')
    setError(null)

    const syncParticipants = () => {
      if (disposed || !call) return
      const streams = new Map<string, MediaStream>()
      for (const participant of Object.values(call.participants())) {
        if (participant.local) continue
        const identityId = participantIdentity(participant)
        const videoTrack = participant.tracks.video.persistentTrack
        if (!identityId || !videoTrack || videoTrack.readyState !== 'live') continue
        streams.set(identityId, new MediaStream([videoTrack]))
      }
      setRemoteStreams(streams)
    }

    const syncLocal = () => {
      if (disposed || !call) return
      const localTrack = call.participants().local?.tracks.video.persistentTrack
      setProcessedLocalStream(localTrack ? new MediaStream([localTrack]) : null)
    }

    const syncSubscriptions = () => {
      if (disposed || !call) return
      const updates = Object.fromEntries(
        Object.values(call.participants())
          .filter((participant) => !participant.local)
          .map((participant) => [
            participant.session_id,
            {
              setSubscribedTracks: visibleRef.current.has(participantIdentity(participant) ?? '')
                ? { audio: false, video: true, screenAudio: false, screenVideo: false }
                : false,
            },
          ]),
      )
      if (Object.keys(updates).length) call.updateParticipants(updates)
    }

    const onJoined = () => {
      if (!call || disposed) return
      retryCountRef.current = 0
      setState('joined')
      call.setLocalAudio(false)
      // Apply whatever changed while the join was in flight.
      call.setLocalVideo(cameraOnRef.current)
      const currentTrack = localStreamRef.current?.getVideoTracks()[0]
      if (currentTrack !== track) {
        void call.setInputDevicesAsync({ videoSource: currentTrack ?? false }).catch(() => undefined)
      }
      if (lastEffectRef.current.effect !== 'none') {
        void applyBackground(lastEffectRef.current.effect, lastEffectRef.current.source)
      }
      syncSubscriptions()
      syncParticipants()
      syncLocal()
      // Meeting tokens expire after four hours; rejoin with a fresh one shortly before.
      if (renewTimer) window.clearTimeout(renewTimer)
      renewTimer = window.setTimeout(
        () => setGeneration((current) => current + 1),
        3.75 * 60 * 60 * 1000,
      )
    }
    const onError = () => {
      if (disposed) return
      setState('error')
      setError('Daily video could not connect')
      if (retryTimer !== null || retryCountRef.current >= 3) return
      retryCountRef.current += 1
      retryTimer = window.setTimeout(
        () => setGeneration((current) => current + 1),
        5_000 * retryCountRef.current,
      )
    }

    const connect = async () => {
      const accessResponse = await fetch('/api/media/daily-access', {
        credentials: 'same-origin',
      })
      if (!accessResponse.ok) throw new Error('Video access is unavailable')
      const access = (await accessResponse.json()) as DailyAccess
      const { default: DailyIframe } = await import('@daily-co/daily-js')
      if (disposed) return
      call = DailyIframe.createCallObject({
        audioSource: false,
        videoSource: track ?? false,
        startAudioOff: true,
        startVideoOff: !cameraOn || !track,
        subscribeToTracksAutomatically: false,
        allowMultipleCallInstances: true,
        // avoidEval keeps the call machine compatible with a strict production CSP
        dailyConfig: { keepCamIndicatorLightOn: true, avoidEval: true },
        sendSettings: { video: 'quality-optimized' },
      })
      callRef.current = call
      call.on('joined-meeting', onJoined)
      call.on('participant-joined', syncSubscriptions)
      call.on('participant-joined', syncParticipants)
      call.on('participant-updated', syncParticipants)
      call.on('participant-left', syncParticipants)
      call.on('track-started', syncParticipants)
      call.on('track-stopped', syncParticipants)
      call.on('input-settings-updated', syncLocal)
      call.on('error', onError)
      await call.join({
        url: access.url,
        // daily-js rejects `token: undefined`; only pass it when the server minted one
        ...(access.token ? { token: access.token } : {}),
        userName: identity.name,
        userData: { identityId: identity.id },
        audioSource: false,
        videoSource: track ?? false,
        startAudioOff: true,
        startVideoOff: !cameraOn || !track,
        sendSettings: { video: 'quality-optimized' },
      })
    }

    void connect().catch((joinError: unknown) => {
      if (disposed) return
      onError()
      setError(
        joinError instanceof Error
          ? joinError.message
          : 'Daily video could not connect',
      )
    })

    return () => {
      disposed = true
      if (renewTimer) window.clearTimeout(renewTimer)
      if (retryTimer) window.clearTimeout(retryTimer)
      callRef.current = null
      setRemoteStreams(new Map())
      setProcessedLocalStream(null)
      const activeCall = call
      if (!activeCall) return
      void activeCall
        .leave()
        .catch(() => undefined)
        .finally(() => {
          if (!activeCall.isDestroyed()) void activeCall.destroy()
        })
    }
  }, [enabled, identity?.id, generation])

  return { remoteStreams, localStream: processedLocalStream, state, error, backgroundEffect, applyBackground }
}
